/**
 * Sentry React SDK bootstrap with LGPD-compliant PII scrubbing (OBS-02, D-20).
 *
 * Mirrors the backend's `app/core/sentry.py` scrubber so that events emitted
 * from the browser carry the same guarantees: CPF/CNPJ/email/card patterns
 * never reach Sentry's SaaS in plaintext, and Authorization / Cookie / apikey
 * headers are replaced with `[Filtered]` before the event leaves the browser.
 *
 * `initSentry()` is a no-op when `VITE_SENTRY_DSN` is unset so local dev and
 * preview builds never emit to the production Sentry project.
 *
 * References:
 * - Sentry React SDK: https://docs.sentry.io/platforms/javascript/guides/react/
 * - LGPD Art. 46/48 (medidas técnicas) — D-20 in 18-CONTEXT.md
 * - Threat model: T-18-06 (user.email), T-18-07 (auth headers) in 18-04-PLAN.md
 */
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";

// PII regex patterns applied to every string encountered during scrubbing.
// Mirrors backend/app/core/sentry.py _PII_PATTERNS (CPF/CNPJ with punctuation,
// 13-19 digit card numbers, RFC-5322-ish emails).
const PII_PATTERNS: RegExp[] = [
  /\b\d{3}\.\d{3}\.\d{3}-\d{2}\b/g, // CPF
  /\b\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}\b/g, // CNPJ
  /\b(?:\d[ -]*?){13,19}\b/g, // Card numbers
  /[\w.-]+@[\w.-]+\.\w+/g, // Email
];

// Headers redacted wholesale — never safe to ship verbatim to a third party.
const SENSITIVE_HEADERS = new Set<string>([
  "authorization",
  "cookie",
  "set-cookie",
  "apikey",
  "cf-access-jwt-assertion",
  "x-api-key",
]);

// Keys redacted wholesale regardless of nesting depth or surrounding context.
const SENSITIVE_KEYS = new Set<string>([
  "password",
  "senha",
  "token",
  "secret",
  "api_key",
  "apikey",
  "cpf",
  "cnpj",
]);

function scrubString(value: string): string {
  let out = value;
  for (const pattern of PII_PATTERNS) {
    out = out.replace(pattern, "[REDACTED]");
  }
  return out;
}

function scrub(obj: unknown): unknown {
  if (typeof obj === "string") {
    return scrubString(obj);
  }
  if (Array.isArray(obj)) {
    return obj.map(scrub);
  }
  if (obj && typeof obj === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      out[key] = SENSITIVE_KEYS.has(key.toLowerCase()) ? "[Filtered]" : scrub(value);
    }
    return out;
  }
  return obj;
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    // eslint-disable-next-line no-console
    console.info("Sentry disabled (VITE_SENTRY_DSN not configured)");
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? "production",
    release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA,
    sendDefaultPii: false,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    beforeSend(event) {
      // Filter sensitive request headers (T-18-07).
      if (event.request?.headers) {
        const headers = event.request.headers as Record<string, string>;
        for (const key of Object.keys(headers)) {
          if (SENSITIVE_HEADERS.has(key.toLowerCase())) {
            headers[key] = "[Filtered]";
          }
        }
      }

      // Redact user email (T-18-06, LGPD D-20).
      if (event.user && typeof event.user === "object" && "email" in event.user) {
        event.user.email = "[REDACTED]";
      }

      // Scrub request body + query string for CPF/CNPJ/card/email patterns
      // and sensitive keys (password, token, secret, ...).
      if (event.request) {
        if (event.request.data !== undefined) {
          event.request.data = scrub(event.request.data);
        }
        if (typeof event.request.query_string === "string") {
          event.request.query_string = scrubString(event.request.query_string);
        }
        if (event.request.cookies) {
          // Sentry types cookies as Record<string,string>; replace every value
          // with a placeholder rather than mutating to a scalar string.
          const cookies = event.request.cookies as Record<string, string>;
          for (const key of Object.keys(cookies)) {
            cookies[key] = "[Filtered]";
          }
        }
      }

      // Scrub free-form extras attached via Sentry.setExtra().
      if (event.extra) {
        event.extra = scrub(event.extra) as Record<string, unknown>;
      }

      return event;
    },
  });
}
